/**
 * Public Menu Page
 *
 * Features:
 * - Compact sticky search bar + filter panel
 * - Staff Picks row (featured items) above the accordion
 * - Category accordion (multiple can be open simultaneously)
 * - Sticky category nav with scroll-spy via IntersectionObserver
 * - Default sort preserves Tammy's admin ordering; other sorts available
 * - Product card image + name links to /product/:id for richer browsing
 * - Cart quantity controls, special-item modals, deep-link support
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BreadcrumbSchema } from '../components/StructuredData';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { motion } from 'framer-motion';
import {
  ChevronDownIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { HiSearch, HiX } from 'react-icons/hi';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ShareButton from '../components/ShareButton';
import ProductCustomization, { ProductCustomizations } from '../components/ProductCustomization';
import StuffedCookieSelectionModal from '../components/StuffedCookieSelectionModal';
import ButterTartSelectionModal from '../components/ButterTartSelectionModal';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { menuItemElementId } from '../utils/menuDeepLink';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';

// ── Types ────────────────────────────────────────────────────────────────────

interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  image_url: string | null;
  /** CSS object-position when set via All Images / image-display-settings */
  image_object_position?: string | null;
  is_available: boolean;
  is_featured: boolean;
  /** When true, show "Gluten free" badge and include in gluten-free filter */
  is_gluten_free?: boolean;
  tax_applies?: boolean | null;
}

interface MenuCategory {
  id: string;
  name: string;
  icon: string;
  note?: string;
  items: MenuItem[];
}

/** 'default' preserves the display order Tammy set in the admin panel. */
type SortOption = 'default' | 'name' | 'price-low' | 'price-high';

// ── Static data ──────────────────────────────────────────────────────────────

const categoryIconsFallback: Record<string, string> = {
  'Custom Cakes': '🎂',
  'Cheesecakes': '🍰',
  'Stuffed Cookies': '🍪',
  'Cookies': '🍪',
  'Cookie Sandwiches': '🍪',
  'Tarts': '🥧',
  'Bars, Squares & Treats': '🍫',
  'Sandwiches & Savory': '🥖',
  'Gluten Free': '🌾',
  'Pastries': '🥐',
  'Muffins': '🧁',
  'Valentines': '💝',
  'Seasonal': '🍂',
  'Soups': '🍲',
};

const categoryNotes: Record<string, string> = {
  'Custom Cakes': 'Please give us 72 hour notice on any cake order. Thank you!',
  'Stuffed Cookies': "You'll pick your flavours when you add to cart.",
  'Tarts': "You'll pick Plain or Pecan when you add to cart.",
  'Drinks': "Pick your size and milk when you add to cart.",
  'Soups': "Pick your size and soup when you add to cart.",
};

const formatPrice = (price: number) => `$${price.toFixed(2)}`;

// ── Component ────────────────────────────────────────────────────────────────

export default function Menu() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.menu,
    og: {
      type: 'website',
      title: PAGE_SEO.menu.title,
      description: PAGE_SEO.menu.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const itemQueryParam = searchParams.get('item');

  const [allCategories, setAllCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Which categories are expanded in the accordion (multiple allowed).
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // Which category is highlighted in the sticky nav (driven by scroll spy).
  const [visibleCategory, setVisibleCategory] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState<{ min: number; max: number } | null>(null);
  const [glutenFreeOnly, setGlutenFreeOnly] = useState(false);
  // Default preserves Tammy's admin ordering; other options sort within each category.
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const [showFilters, setShowFilters] = useState(false);

  const { items: cartItems, addItem, updateQuantity, totalItems } = useCart();
  const [customizationItem, setCustomizationItem] = useState<MenuItem | null>(null);
  const [stuffedCookieItem, setStuffedCookieItem] = useState<MenuItem | null>(null);
  const [butterTartItem, setButterTartItem] = useState<MenuItem | null>(null);

  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // ── Data fetch ───────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const [menuRes, categoriesRes] = await Promise.all([
          fetch('/api/menu?available_only=true'),
          fetch('/api/menu-categories?active=true'),
        ]);
        if (!menuRes.ok) throw new Error('Failed to load menu');
        const data = await menuRes.json();
        const items: MenuItem[] = data.items || [];

        const catData = categoriesRes.ok ? await categoriesRes.json() : { categories: [] };
        const activeCategories = (catData.categories || []) as {
          name: string;
          display_order: number;
          icon: string | null;
        }[];
        const activeMap = new Map(
          activeCategories.map((c) => [
            c.name,
            { display_order: c.display_order, icon: c.icon || categoryIconsFallback[c.name] || '🧁' },
          ])
        );

        const grouped = items
          .filter((item) => item.is_available)
          .reduce(
            (acc, item) => {
              const cat = item.category || 'Other';
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(item);
              return acc;
            },
            {} as Record<string, MenuItem[]>
          );

        const categoryList: MenuCategory[] = Object.entries(grouped)
          .filter(([name]) => activeMap.has(name))
          .map(([name, catItems]) => {
            const meta = activeMap.get(name)!;
            return {
              id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
              name,
              icon: meta.icon,
              note: categoryNotes[name],
              items: catItems,
            };
          });

        categoryList.sort((a, b) => {
          const orderA = activeMap.get(a.name)?.display_order ?? 999;
          const orderB = activeMap.get(b.name)?.display_order ?? 999;
          return orderA - orderB;
        });

        // Stuffed Cookies: fetch live flavor note from Product Options
        const stuffedIndex = categoryList.findIndex((c) => c.name === 'Stuffed Cookies');
        if (stuffedIndex >= 0) {
          try {
            const optRes = await fetch('/api/product-options?category=Stuffed Cookies');
            const optData = await optRes.json();
            const flavorGroup =
              optData.groups?.find(
                (g: { name?: string; category_name?: string }) =>
                  g.name?.toLowerCase().includes('flavor') || g.category_name === 'Stuffed Cookies'
              ) ?? optData.groups?.[0];
            const flavorNames =
              flavorGroup?.options
                ?.filter((o: { is_available?: boolean }) => o.is_available !== false)
                ?.map((o: { name: string }) => o.name) ?? [];
            const note =
              flavorNames.length > 0
                ? `Flavors: ${flavorNames.join(', ')}. Select your flavors when ordering!`
                : 'Select your flavors when ordering!';
            categoryList[stuffedIndex] = { ...categoryList[stuffedIndex], note };
          } catch {
            categoryList[stuffedIndex] = {
              ...categoryList[stuffedIndex],
              note: 'Select your flavors when ordering!',
            };
          }
        }

        setAllCategories(categoryList);
      } catch (err) {
        console.error('Menu load failed:', err);
        setError(publicLoadErrorMessage());
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  // ── Deep link: /menu?item=<id>, /menu?category=Stuffed+Cookies, or /menu#<id|category-slug> ──

  const categoryQueryParam = searchParams.get('category');

  useEffect(() => {
    if (loading || allCategories.length === 0) return;

    const fromQuery = itemQueryParam;
    const fromHash = location.hash?.replace(/^#/, '') || '';
    const targetId = (fromQuery || fromHash || '').trim();
    const categoryTarget = (categoryQueryParam || '').trim();

    // Category deep link (birthday promo → stuffed cookies section).
    if (categoryTarget || (fromHash && allCategories.some((c) => c.id === fromHash))) {
      const slugOrName = categoryTarget || fromHash;
      const foundCat = allCategories.find(
        (cat) =>
          cat.id === slugOrName.toLowerCase().replace(/[^a-z0-9]+/g, '-') ||
          cat.name.toLowerCase() === slugOrName.toLowerCase()
      );
      if (foundCat) {
        setSearchTerm('');
        setPriceFilter(null);
        setGlutenFreeOnly(false);
        setSortBy('default');
        setExpandedCategories((prev) => new Set([...prev, foundCat.id]));
        if (categoryQueryParam) setSearchParams({}, { replace: true });
        if (fromHash) {
          window.history.replaceState(null, '', `${location.pathname}${location.search}`);
        }
        const scrollTimer = window.setTimeout(() => {
          const el = categoryRefs.current[foundCat.id];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 280);
        return () => window.clearTimeout(scrollTimer);
      }
    }

    if (!targetId) return;

    const foundCat = allCategories.find((cat) => cat.items.some((i) => i.id === targetId));
    if (!foundCat) return;

    setSearchTerm('');
    setPriceFilter(null);
    setGlutenFreeOnly(false);
    setSortBy('default');
    // Open the target category
    setExpandedCategories((prev) => new Set([...prev, foundCat.id]));

    if (fromQuery) setSearchParams({}, { replace: true });
    if (fromHash) {
      window.history.replaceState(null, '', `${location.pathname}${location.search}`);
    }

    const scrollTimer = window.setTimeout(() => {
      const el = document.getElementById(menuItemElementId(targetId));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2');
        window.setTimeout(() => {
          el.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2');
        }, 2600);
      }
    }, 280);

    return () => window.clearTimeout(scrollTimer);
  }, [
    loading,
    allCategories,
    itemQueryParam,
    categoryQueryParam,
    location.hash,
    location.pathname,
    location.search,
    setSearchParams,
  ]);

  // ── Scroll spy: highlight nav pill for the category currently on screen ──

  useEffect(() => {
    if (allCategories.length === 0) return;

    // Detect the section whose header is in the upper third of the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.categoryId;
            if (id) setVisibleCategory(id);
          }
        });
      },
      { rootMargin: '-5% 0px -70% 0px', threshold: 0 }
    );

    Object.entries(categoryRefs.current).forEach(([id, el]) => {
      if (el) {
        el.dataset.categoryId = id;
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [allCategories]);

  // ── Derived: filtered + sorted categories ───────────────────────────────

  const categories = useMemo(() => {
    const processed = allCategories.map((cat) => {
      let filteredItems = [...cat.items];

      if (glutenFreeOnly) filteredItems = filteredItems.filter((i) => i.is_gluten_free === true);

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        filteredItems = filteredItems.filter(
          (i) =>
            i.name.toLowerCase().includes(term) ||
            (i.description || '').toLowerCase().includes(term) ||
            i.category.toLowerCase().includes(term)
        );
      }

      if (priceFilter) {
        filteredItems = filteredItems.filter(
          (i) => i.price >= priceFilter.min && i.price <= priceFilter.max
        );
      }

      // Sort — 'default' leaves items in the order returned by the API (Tammy's ordering)
      if (sortBy !== 'default') {
        filteredItems.sort((a, b) => {
          if (sortBy === 'name') return a.name.localeCompare(b.name);
          if (sortBy === 'price-low') return a.price - b.price;
          if (sortBy === 'price-high') return b.price - a.price;
          return 0;
        });
      }

      return { ...cat, items: filteredItems };
    });

    return processed.filter((cat) => cat.items.length > 0);
  }, [allCategories, searchTerm, priceFilter, glutenFreeOnly, sortBy]);

  // ── Derived: featured items for Staff Picks row ─────────────────────────

  const featuredItems = useMemo(
    () => allCategories.flatMap((c) => c.items.filter((i) => i.is_featured)),
    [allCategories]
  );

  const priceRange = useMemo(() => {
    if (allCategories.length === 0) return { min: 0, max: 100 };
    const allPrices = allCategories.flatMap((cat) => cat.items.map((i) => i.price));
    return { min: Math.floor(Math.min(...allPrices)), max: Math.ceil(Math.max(...allPrices)) };
  }, [allCategories]);

  // ── Category accordion helpers ───────────────────────────────────────────

  /** Scroll to a category section and expand it. */
  const scrollToCategory = useCallback((categoryId: string) => {
    const el = categoryRefs.current[categoryId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedCategories((prev) => new Set([...prev, categoryId]));
    }
  }, []);

  /** Toggle a category open/closed in the accordion. */
  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  // ── Cart helpers ─────────────────────────────────────────────────────────

  const getCartQuantity = (item: MenuItem) => {
    if (isCustomizable(item)) {
      return cartItems.filter((ci) => ci.id === item.id).reduce((s, ci) => s + ci.quantity, 0);
    }
    return cartItems.find((ci) => ci.id === item.id && !ci.customizations)?.quantity ?? 0;
  };

  const isCustomizable = (item: MenuItem) =>
    item.category === 'Sandwiches & Savory' ||
    item.category === 'Drinks' ||
    item.category === 'Soups';

  const isButterTart = (item: MenuItem) =>
    item.category === 'Tarts' && item.name.toLowerCase().includes('butter tart');

  const handleAddToCart = (item: MenuItem) => {
    if (isCustomizable(item)) { setCustomizationItem(item); return; }
    if (item.category === 'Stuffed Cookies') { setStuffedCookieItem(item); return; }
    if (isButterTart(item)) { setButterTartItem(item); return; }
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
      category: item.category,
      tax_applies: item.tax_applies ?? undefined,
    });
  };

  const handleCustomization = (customizations: ProductCustomizations, extraCost: number) => {
    if (!customizationItem) return;
    const noteParts: string[] = [];
    Object.values(customizations).forEach((group) => {
      if (group.selections.length > 0) {
        noteParts.push(`${group.groupName}: ${group.selections.map((s) => s.name).join(', ')}`);
      }
    });
    addItem({
      id: customizationItem.id,
      name: customizationItem.name,
      price: customizationItem.price + extraCost,
      image: customizationItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
      notes: noteParts.join(' | '),
      customizations,
      category: customizationItem.category,
      tax_applies: customizationItem.tax_applies ?? undefined,
    });
    setCustomizationItem(null);
  };

  const handleUpdateQuantity = (item: MenuItem, newQuantity: number) => {
    if (isCustomizable(item)) return; // Must edit from cart
    const cartItem = cartItems.find((ci) => ci.id === item.id && !ci.customizations);
    if (cartItem) {
      updateQuantity(cartItem.lineId, newQuantity <= 0 ? 0 : newQuantity);
    } else if (newQuantity > 0) {
      addItem(
        {
          id: item.id,
          name: item.name,
          price: item.price,
          image: item.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
          category: item.category,
          tax_applies: item.tax_applies ?? undefined,
        },
        newQuantity
      );
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setPriceFilter(null);
    setGlutenFreeOnly(false);
    setSortBy('default');
  };

  const hasActiveFilters =
    !!searchTerm.trim() || !!priceFilter || glutenFreeOnly || sortBy !== 'default';
  const totalItemsShown = categories.reduce((sum, cat) => sum + cat.items.length, 0);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://nannybbakery.ca/' },
          { name: 'Menu', url: 'https://nannybbakery.ca/menu' },
        ]}
      />

      {/* Floating cart button */}
      {totalItems > 0 && (
        <Link
          to="/order"
          className="fixed bottom-6 right-6 z-40 bg-primary-600 text-white p-4 rounded-full shadow-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <ShoppingCartIcon className="h-6 w-6" />
          <span className="font-bold">{totalItems}</span>
        </Link>
      )}

      {/* ── Compact hero ── */}
      <section className="gradient-primary text-white py-6 pb-5">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-display font-bold mb-2"
          >
            Our Menu
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="text-base text-gray-100 max-w-xl mx-auto"
          >
            Fresh baked goods, custom cakes, and delicious treats. Prices in CAD.
          </motion.p>
        </div>
      </section>

      {/* ── Compact sticky search + filter bar ── */}
      {!loading && !error && allCategories.length > 0 && (
        <section className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
          <div className="container-custom py-2">
            {/* Single row: search — gluten-free toggle — sort — filter icon */}
            <div className="flex items-center gap-2">

              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Gluten-free quick toggle */}
              <label className="flex items-center gap-1.5 cursor-pointer shrink-0 text-sm text-gray-600 select-none">
                <input
                  type="checkbox"
                  checked={glutenFreeOnly}
                  onChange={(e) => setGlutenFreeOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  aria-label="Gluten free only"
                />
                <span className="hidden sm:inline">GF only</span>
              </label>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shrink-0"
                aria-label="Sort menu items"
              >
                <option value="default">Default</option>
                <option value="name">A → Z</option>
                <option value="price-low">Price ↑</option>
                <option value="price-high">Price ↓</option>
              </select>

              {/* Price filter toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-sm transition-colors shrink-0 ${
                  showFilters || !!priceFilter
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Price filter"
                title="Filter by price"
              >
                {/* Funnel icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {priceFilter && (
                  <span className="bg-primary-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">1</span>
                )}
              </button>
            </div>

            {/* Price filter panel (expandable) */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-3 text-sm"
              >
                <span className="text-gray-600 shrink-0">
                  Price: ${priceFilter?.min ?? priceRange.min} – ${priceFilter?.max ?? priceRange.max}
                </span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={priceRange.min} max={priceRange.max}
                    value={priceFilter?.min ?? priceRange.min}
                    onChange={(e) =>
                      setPriceFilter({
                        min: parseInt(e.target.value) || priceRange.min,
                        max: priceFilter?.max ?? priceRange.max,
                      })
                    }
                    className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-400">–</span>
                  <input
                    type="number"
                    min={priceRange.min} max={priceRange.max}
                    value={priceFilter?.max ?? priceRange.max}
                    onChange={(e) =>
                      setPriceFilter({
                        min: priceFilter?.min ?? priceRange.min,
                        max: parseInt(e.target.value) || priceRange.max,
                      })
                    }
                    className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="Max"
                  />
                  {priceFilter && (
                    <button
                      onClick={() => setPriceFilter(null)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Clear price"
                    >
                      <HiX className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-primary-600 hover:underline text-xs ml-auto">
                    Clear all
                  </button>
                )}
              </motion.div>
            )}

            {/* Results count when filtering */}
            {hasActiveFilters && (
              <p className="mt-1 text-xs text-gray-500">
                {totalItemsShown} item{totalItemsShown !== 1 ? 's' : ''}
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Sticky category nav (scroll spy highlights current section) ── */}
      {!loading && !error && categories.length > 3 && (
        <div className="bg-gray-50 border-b border-gray-200 sticky top-[52px] z-20 overflow-x-auto">
          <div className="container-custom py-1.5">
            <div className="flex gap-1.5">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  className={`flex-shrink-0 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    visibleCategory === cat.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {cat.icon} {cat.name} ({cat.items.length})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <section className="section-padding">
          <div className="container-custom text-center py-12">
            <div className="animate-spin w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full mx-auto" />
            <p className="text-gray-500 mt-4">Loading our delicious menu…</p>
          </div>
        </section>
      )}

      {/* ── Error ── */}
      {error && (
        <section className="section-padding">
          <div className="container-custom">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700">{error}</p>
              <button onClick={() => window.location.reload()} className="mt-4 btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Empty ── */}
      {!loading && !error && categories.length === 0 && (
        <section className="section-padding">
          <div className="container-custom text-center py-12">
            <div className="text-6xl mb-4">🧁</div>
            <h2 className="text-2xl font-display font-bold text-gray-900">
              {hasActiveFilters ? 'No items match your filters' : 'Menu Coming Soon'}
            </h2>
            <p className="text-gray-600 mt-2">
              {hasActiveFilters
                ? 'Try adjusting your search or filters.'
                : "We're updating our menu. Please check back soon!"}
            </p>
            {hasActiveFilters ? (
              <button onClick={clearFilters} className="mt-4 btn-primary">Clear Filters</button>
            ) : (
              <a href="tel:7055034966" className="mt-6 btn-primary inline-block">
                Call to Order: (705) 503-4966
              </a>
            )}
          </div>
        </section>
      )}

      {!loading && !error && categories.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">

            {/* ── Staff Picks row (featured items) ── */}
            {featuredItems.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-display font-bold text-gray-900 mb-3 flex items-center gap-2">
                  ⭐ Staff Picks
                </h2>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                  {featuredItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/product/${item.id}`}
                      className="flex-shrink-0 w-44 snap-start bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group"
                    >
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            style={item.image_object_position ? { objectPosition: item.image_object_position } : undefined}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">🧁</div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 mb-1">
                          {item.name}
                        </p>
                        <p className="text-primary-600 font-bold text-sm">{formatPrice(item.price)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* ── Category accordion ── */}
            <div className="space-y-4">
              {categories.map((category, index) => {
                const isExpanded = expandedCategories.has(category.id);
                return (
                  <motion.div
                    key={category.id}
                    ref={(el) => { categoryRefs.current[category.id] = el; }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="card overflow-hidden"
                  >
                    {/* Category header (click to expand/collapse) */}
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full p-4 md:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                          {category.items[0]?.image_url ? (
                            <img
                              src={category.items[0].image_url}
                              alt={category.name}
                              className="w-full h-full object-cover"
                              style={
                                category.items[0].image_object_position
                                  ? { objectPosition: category.items[0].image_object_position }
                                  : undefined
                              }
                            />
                          ) : (
                            <span className="text-3xl">{category.icon}</span>
                          )}
                        </div>
                        <div className="text-left">
                          <h2 className="text-xl md:text-2xl font-display font-bold text-gray-900 flex items-center gap-2">
                            <span>{category.icon}</span>
                            <span>{category.name}</span>
                          </h2>
                          {category.note && (
                            <p className="text-sm text-amber-600 mt-0.5">{category.note}</p>
                          )}
                          <p className="text-sm text-gray-500 mt-0.5">
                            {category.items.length} item{category.items.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <ChevronDownIcon
                        className={`h-6 w-6 text-gray-400 transition-transform shrink-0 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Product grid */}
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-gray-200"
                      >
                        <div className="p-4 md:p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {category.items.map((item) => {
                            const cartQty = getCartQuantity(item);
                            const isCake = category.name === 'Custom Cakes';

                            return (
                              <div
                                key={item.id}
                                id={menuItemElementId(item.id)}
                                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow scroll-mt-32"
                              >
                                {/* Image — links to product detail page */}
                                <div className="aspect-video overflow-hidden bg-gray-100 relative group">
                                  <Link
                                    to={`/product/${item.id}`}
                                    className="block w-full h-full"
                                    tabIndex={-1}
                                    aria-label={`View ${item.name} details`}
                                  >
                                    {item.image_url ? (
                                      <img
                                        src={item.image_url}
                                        alt={item.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        style={
                                          item.image_object_position
                                            ? { objectPosition: item.image_object_position }
                                            : undefined
                                        }
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-5xl">
                                        {category.icon}
                                      </div>
                                    )}
                                  </Link>
                                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ShareButton
                                      title={item.name}
                                      text={`Check out ${item.name} ($${item.price.toFixed(2)}) from Nanny B's Bakery! 🧁`}
                                      url={`https://nannybbakery.ca/product/${encodeURIComponent(item.id)}`}
                                      size="sm"
                                    />
                                  </div>
                                </div>

                                {/* Info + cart */}
                                <div className="p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 min-w-0">
                                      {/* Item name links to product detail page */}
                                      <Link
                                        to={`/product/${item.id}`}
                                        className="font-display font-semibold text-gray-900 hover:text-primary-700 transition-colors"
                                      >
                                        {item.name}
                                      </Link>
                                      {item.is_gluten_free && (
                                        <span className="inline-block mt-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                                          🌾 Gluten free
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-lg font-bold text-primary-600 ml-2 shrink-0">
                                      {formatPrice(item.price)}
                                    </span>
                                  </div>

                                  {item.description && (
                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                      {item.description}
                                    </p>
                                  )}

                                  {isCake ? (
                                    <Link
                                      to="/cake-request"
                                      className="w-full btn-outline text-sm py-2 text-center block"
                                    >
                                      Order Custom Cake
                                    </Link>
                                  ) : cartQty === 0 ? (
                                    <button
                                      onClick={() => handleAddToCart(item)}
                                      className="w-full btn-primary text-sm py-2"
                                      data-testid={`add-to-cart-${item.id}`}
                                    >
                                      Add to Cart
                                    </button>
                                  ) : (
                                    <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
                                      <button
                                        onClick={() => handleUpdateQuantity(item, cartQty - 1)}
                                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                        aria-label="Decrease quantity"
                                      >
                                        <MinusIcon className="h-5 w-5 text-gray-600" />
                                      </button>
                                      <span className="font-semibold text-gray-900">{cartQty}</span>
                                      <button
                                        onClick={() => handleUpdateQuantity(item, cartQty + 1)}
                                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                        aria-label="Increase quantity"
                                      >
                                        <PlusIcon className="h-5 w-5 text-gray-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Modals ── */}

      {customizationItem && (
        <ProductCustomization
          isOpen={!!customizationItem}
          onClose={() => setCustomizationItem(null)}
          onConfirm={handleCustomization}
          itemName={customizationItem.name}
          itemPrice={customizationItem.price}
          category={customizationItem.category}
        />
      )}

      {stuffedCookieItem && (
        <StuffedCookieSelectionModal
          isOpen
          onClose={() => setStuffedCookieItem(null)}
          onConfirm={(selection) => {
            addItem({
              id: stuffedCookieItem.id,
              name: stuffedCookieItem.name,
              price: stuffedCookieItem.price,
              image: stuffedCookieItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
              notes: selection,
              category: stuffedCookieItem.category,
              tax_applies: stuffedCookieItem.tax_applies ?? undefined,
            });
            setStuffedCookieItem(null);
          }}
          productId={stuffedCookieItem.id}
          productName={stuffedCookieItem.name}
          productPrice={stuffedCookieItem.price}
        />
      )}

      {butterTartItem && (
        <ButterTartSelectionModal
          isOpen
          onClose={() => setButterTartItem(null)}
          onConfirm={(selection) => {
            addItem({
              id: butterTartItem.id,
              name: butterTartItem.name,
              price: butterTartItem.price,
              image: butterTartItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
              notes: selection,
              category: butterTartItem.category,
              tax_applies: butterTartItem.tax_applies ?? undefined,
            });
            setButterTartItem(null);
          }}
          productId={butterTartItem.id}
          productName={butterTartItem.name}
          productPrice={butterTartItem.price}
        />
      )}

      {/* ── Bottom CTA ── */}
      <section className="bg-white section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
            {totalItems > 0
              ? `You have ${totalItems} item${totalItems > 1 ? 's' : ''} in your cart!`
              : 'Ready to Order?'}
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {totalItems > 0
              ? 'Continue adding items or proceed to checkout.'
              : 'Add items to your cart above, or call us for custom cake orders.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {totalItems > 0 && (
              <Link to="/order" className="btn-primary">View Cart &amp; Checkout</Link>
            )}
            <a href="tel:7055034966" className={totalItems > 0 ? 'btn-outline' : 'btn-primary'}>
              Call (705) 503-4966
            </a>
            <Link to="/contact" className="btn-outline">Send us a message</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
